// Pure helpers for the production-plan screens (Stage 2A). No React, no API.
import type { PlanAction, PlanDemandRow, PlanInput, PlanItem, PlanStatus, ProductionPlan } from './planTypes'

/** The buttons a plan offers, primary first. Mirrors planTransitionAllowed /
 *  planSendToStoreNext: "Send to store" leads on a locked plan, and a plan the
 *  chef started from kitchen stock before sending can still be sent ("sent"
 *  is sent_at — a plan whose kitchen had everything is sent with no request).
 *  Once sent, the way to finish is "Event done — wrap up" (Batch 2), which
 *  marks the plan done if it has to and opens the wrap-up. */
export function planActions(p: Pick<ProductionPlan, 'status' | 'produce' | 'batches'> & { sent_at?: string | null; wrap_up?: unknown }): PlanAction[] {
  const toCook = (p.produce ?? []).some((x) => x.produce_qty > 1e-6)
  const planned = new Set((p.batches ?? []).filter((b) => b.status !== 'cancelled').map((b) => b.prep_item_id))
  const moreToPlan = (p.produce ?? []).some((x) => x.produce_qty > 1e-6 && !planned.has(x.prep_item_id))
  switch (p.status) {
    case 'draft':
      return ['confirm', 'cancel']
    case 'confirmed':
      return [...(['send-to-store'] as PlanAction[]), ...(toCook && moreToPlan ? (['plan-batches'] as PlanAction[]) : []), 'start', 'reopen', 'cancel']
    case 'requisitioned':
      return [...(toCook && moreToPlan ? (['plan-batches'] as PlanAction[]) : []), 'start', 'wrap-up', 'cancel']
    case 'in_production':
      return [
        ...(!p.sent_at ? (['send-to-store'] as PlanAction[]) : []),
        ...(toCook && moreToPlan ? (['plan-batches'] as PlanAction[]) : []),
        'wrap-up',
        'cancel',
      ]
    case 'done':
      return p.wrap_up ? [] : ['wrap-up']
    default:
      return []
  }
}

/** Raw items only: what the chef will ask the store for, starting from the suggestion. */
export function initialAsks(demand: PlanDemandRow[]): Record<string, number> {
  const out: Record<string, number> = {}
  for (const d of demand) if (!d.is_prep) out[d.stock_item_id] = d.suggested_request_qty
  return out
}

/** The send-to-store body: every raw line with the chef's amount (0 drops it). */
export function askLines(demand: PlanDemandRow[], asks: Record<string, number>): { stock_item_id: string; qty: number }[] {
  return demand
    .filter((d) => !d.is_prep)
    .map((d) => ({ stock_item_id: d.stock_item_id, qty: Math.max(0, Number(asks[d.stock_item_id] ?? d.suggested_request_qty) || 0) }))
}

export interface DraftDish {
  product_id: string
  name: string
  image_url?: string | null
  qty: number
}

/** Adds a dish (or one more of it). */
export function addDish(list: DraftDish[], dish: Omit<DraftDish, 'qty'>, qty = 1): DraftDish[] {
  const i = list.findIndex((d) => d.product_id === dish.product_id)
  if (i < 0) return [...list, { ...dish, qty }]
  return list.map((d, j) => (j === i ? { ...d, qty: d.qty + qty } : d))
}

/** Sets a dish's portions (whole numbers). Zero keeps the dish on the list —
 *  a cleared box is still being typed in; only removeDish takes it off. */
export function setDishQty(list: DraftDish[], productId: string, qty: number): DraftDish[] {
  const q = Math.max(0, Math.round(Number.isFinite(qty) ? qty : 0))
  return list.map((d) => (d.product_id === productId ? { ...d, qty: q } : d))
}

export function removeDish(list: DraftDish[], productId: string): DraftDish[] {
  return list.filter((d) => d.product_id !== productId)
}

/** A dish with no portions set blocks saving ("Enter how many"). */
export const dishesMissingQty = (list: { qty: number }[]) => list.some((d) => !(d.qty > 0))

export function dishesFromItems(items: PlanItem[] | undefined): DraftDish[] {
  return (items ?? []).map((i) => ({ product_id: i.product_id, name: i.product_name, image_url: i.image_url, qty: i.qty }))
}

export function planInputFrom(d: { for_date: string; title: string; notes: string; order_type: PlanInput['order_type']; dishes: DraftDish[]; extras?: DraftExtra[] }): PlanInput {
  return {
    for_date: d.for_date,
    title: d.title.trim() || undefined,
    notes: d.notes.trim() || undefined,
    order_type: d.order_type,
    items: d.dishes.filter((x) => x.qty > 0).map((x) => ({ product_id: x.product_id, qty: x.qty })),
    extras: (d.extras ?? []).filter((x) => x.qty > 0).map((x) => ({ stock_item_id: x.stock_item_id, qty: x.qty })),
  }
}

// ── Stage 2B: packaging & equipment ─────────────────────────────────────────

export interface DraftExtra {
  stock_item_id: string
  name: string
  unit: string
  qty: number
  is_returnable: boolean
}

/** Adds an item (or one more of it). Counted items step by one. */
export function addExtra(list: DraftExtra[], item: Omit<DraftExtra, 'qty'>, qty = 1): DraftExtra[] {
  const i = list.findIndex((x) => x.stock_item_id === item.stock_item_id)
  if (i < 0) return [...list, { ...item, qty }]
  return list.map((x, j) => (j === i ? { ...x, qty: Math.round((x.qty + qty) * 1000) / 1000 } : x))
}

export function setExtraQty(list: DraftExtra[], id: string, qty: number): DraftExtra[] {
  const q = Math.max(0, Math.round(qty * 1000) / 1000)
  return q === 0 ? list.filter((x) => x.stock_item_id !== id) : list.map((x) => (x.stock_item_id === id ? { ...x, qty: q } : x))
}

/** Items a chef may add by hand: never prep items (they come from dishes). */
export function pickableExtras<T extends { name: string; is_prep?: boolean }>(items: T[], search: string, limit = 40): T[] {
  const q = search.trim().toLowerCase()
  return items.filter((i) => !i.is_prep && (!q || i.name.toLowerCase().includes(q))).slice(0, limit)
}

/** Whether "Send equipment back" has anything to do. */
export function hasEquipmentOut(returnables: { outstanding: number }[] | undefined): boolean {
  return (returnables ?? []).some((r) => r.outstanding > 1e-6)
}

export function isEditable(status: PlanStatus): boolean {
  return status === 'draft'
}

/** "2.5", "0.334", "12" — quantities for people, not machines. */
export function fmtAmount(n: number): string {
  const r = Math.round(n * 1000) / 1000
  return Number.isInteger(r) ? String(r) : r.toFixed(3).replace(/0+$/, '').replace(/\.$/, '')
}

const escape = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!)

/**
 * A printable demand sheet: one self-contained HTML page with big rows, for
 * the kitchen wall and the store. Opened in its own window so the admin
 * sidebar never prints.
 */
export function planSheetHtml(p: ProductionPlan, labels: {
  forDate: string; dishes: string; sheet: string; colItem: string; colNeeded: string; colAsk: string; makeTitle: string; colMake: string
  customerNote?: string; planNotes?: string; bookingCancelled?: string
}): string {
  const rows = (p.demand ?? []).filter((d) => !d.is_prep)
  const make = (p.produce ?? []).filter((x) => x.produce_qty > 1e-6)
  const dishRows = (p.items ?? [])
    .map((i) => `<tr><td>${escape(i.product_name)}${i.note ? `<div class="note">${escape(i.note)}</div>` : ''}</td><td class="n">${i.qty}</td></tr>`)
    .join('')
  const cancelled = bookingCancelledOnPlan(p) && labels.bookingCancelled ? `<p class="warn"><strong>${escape(labels.bookingCancelled)}</strong></p>` : ''
  const customer = p.booking?.notes ? `<p class="warn"><strong>${escape(labels.customerNote ?? '')}:</strong> ${escape(p.booking.notes)}</p>` : ''
  const notes = p.notes ? `<h2>${escape(labels.planNotes ?? '')}</h2><p class="pre">${escape(p.notes)}</p>` : ''
  const needRows = rows
    .map((d) => `<tr><td>${escape(d.name)}</td><td class="n">${fmtAmount(d.net_qty)} ${escape(d.unit)}</td><td class="n">${fmtAmount(d.suggested_request_qty)} ${escape(d.unit)}</td></tr>`)
    .join('')
  const makeRows = make.map((x) => `<tr><td>${escape(x.name)}</td><td class="n">${fmtAmount(x.produce_qty)} ${escape(x.unit)}</td></tr>`).join('')
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escape(p.plan_no)}</title><style>
body{font-family:system-ui,sans-serif;margin:24px;color:#111}h1{font-size:26px;margin:0 0 4px}h2{font-size:20px;margin:24px 0 8px}
p{margin:0 0 12px;font-size:16px}table{width:100%;border-collapse:collapse;font-size:18px}td,th{border-bottom:1px solid #999;padding:10px 6px;text-align:left}
th{font-size:14px;text-transform:uppercase;color:#444}.n{text-align:right;white-space:nowrap}
.note{font-size:15px;font-weight:600;margin-top:2px}.warn{border:2px solid #111;padding:8px 10px}.pre{white-space:pre-wrap;font-size:14px}
</style></head><body>
<h1>${escape(p.plan_no)}${p.title ? ' — ' + escape(p.title) : ''}</h1>
<p>${escape(labels.forDate)}: <strong>${escape(p.for_date)}</strong></p>
${cancelled}${customer}<h2>${escape(labels.dishes)}</h2><table>${dishRows}</table>
<h2>${escape(labels.sheet)}</h2><table><tr><th>${escape(labels.colItem)}</th><th class="n">${escape(labels.colNeeded)}</th><th class="n">${escape(labels.colAsk)}</th></tr>${needRows}</table>
${make.length ? `<h2>${escape(labels.makeTitle)}</h2><table><tr><th>${escape(labels.colItem)}</th><th class="n">${escape(labels.colMake)}</th></tr>${makeRows}</table>` : ''}
${notes}</body></html>`
}

/** A plan whose booking was cancelled but which could not be cancelled
 *  itself (the store had sent stock): the chef must check, not cook. */
export function bookingCancelledOnPlan(p: Pick<ProductionPlan, 'status' | 'booking'>): boolean {
  return p.booking?.status === 'cancelled' && p.status !== 'cancelled'
}

/** YYYY-MM-DD of the day after `now`, in the device's own time zone (a
 *  UTC-based date is a day off in Pakistan before 5 AM). */
export function localTomorrow(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** How the sheet's last column behaves: editable before sending, the real
 *  request after, the suggestion on a draft. "Store has" is live stock, so it
 *  is shown only while the chef is still deciding. */
export function sheetMode(status: PlanStatus, sent: boolean): { ask: 'edit' | 'asked' | 'suggested'; showStore: boolean } {
  if (sent) return { ask: 'asked', showStore: false }
  // Not sent yet: a confirmed plan, or one already cooking from kitchen stock.
  if (status === 'confirmed' || status === 'in_production') return { ask: 'edit', showStore: true }
  if (status === 'draft') return { ask: 'suggested', showStore: true }
  return { ask: 'suggested', showStore: false }
}

/** Batch 2: what a batch card says it is for — "Khan wedding · Sat 1 Nov",
 *  or the plan number when the plan has no name; null when not for a plan. */
export function batchPlanLabel(b: { plan_no?: string | null; plan_title?: string | null; plan_for_date?: string | null }, fmtDate: (d: string) => string): string | null {
  if (!b.plan_no) return null
  const name = b.plan_title?.trim() || b.plan_no
  return b.plan_for_date ? `${name} · ${fmtDate(b.plan_for_date)}` : name
}

/** Batch 2: the dishes the sheet has no recipe for (from the engine's
 *  product_has_no_recipe warnings), named for the screen. */
export function noRecipeDishes(items: { product_id: string; name: string }[], warnings: { code: string; product_id?: string }[] | undefined): { product_id: string; name: string }[] {
  const ids = new Set((warnings ?? []).filter((w) => w.code === 'product_has_no_recipe' && w.product_id).map((w) => w.product_id as string))
  const seen = new Set<string>()
  return items.filter((i) => ids.has(i.product_id) && !seen.has(i.product_id) && seen.add(i.product_id))
}

/** Stage 2D: the "Kitchen has" note when some stock is not free for this plan. */
export function kitchenNote(d: { kitchen_total?: number; reserved_elsewhere?: number; expired_before_event?: number }): { total: number; reserved: number; expired: number } | null {
  const reserved = d.reserved_elsewhere ?? 0
  const expired = d.expired_before_event ?? 0
  if (reserved <= 1e-6 && expired <= 1e-6) return null
  return { total: d.kitchen_total ?? 0, reserved, expired }
}

/** Go-live polish: what "Kitchen has" shows. On a locked plan the sheet's
 *  kitchen_on_hand is the figure counted when it was locked; the main number
 *  is today's free amount instead, with the counted one underneath when it
 *  differs. A draft (and the preview) is live already. */
export function kitchenHas(d: { kitchen_on_hand: number; kitchen_free?: number }, locked: boolean): { main: number; counted: number | null } {
  if (!locked || d.kitchen_free == null) return { main: d.kitchen_on_hand, counted: null }
  return { main: d.kitchen_free, counted: Math.abs(d.kitchen_free - d.kitchen_on_hand) > 1e-6 ? d.kitchen_on_hand : null }
}
